/**
 * App Component Variant Definitions
 *
 * Uses tailwind-variants tv({ extend }) pattern over HeroUI v3 base variants.
 * Each definition extends the corresponding @heroui/styles variant function,
 * inheriting BEM CSS classes and enabling custom Rosé Pine additions.
 *
 * ## Architecture Note
 *
 * These style functions are EXTENSION POINTS for adding Rosé Pine customizations
 * on top of HeroUI's base variant system. They are NOT used as className in the
 * App* wrapper components because:
 *
 * 1. HeroUI v3 components internally call their own variant functions when you
 *    pass variant/size/color props. Passing the same classes via className would
 *    produce duplicate BEM classes in the DOM.
 * 2. `cx()` (from tailwind-variants) is a concatenator, NOT a deduplicator —
 *    it does not remove duplicate class names.
 *
 * ### Correct patterns:
 *
 * **For App* wrapper components**: Pass variant/size/color as HeroUI props only.
 * Do NOT pass className with variant-computed classes.
 *
 * **For custom Rosé Pine additions**: When adding styles that are NOT part of
 * HeroUI's variant system (e.g., custom shadows, font tweaks), define them in
 * a SEPARATE tv() with only the additional classes — do not use tv({ extend })
 * since extend includes ALL HeroUI classes which would duplicate.
 *
 * **For external consumers**: These functions can be used outside of App*
 * components to compute class strings for non-HeroUI elements.
 *
 * @see https://v3.heroui.com/docs/react/styling/composition
 */

import {
  tv,
  buttonVariants,
  cardVariants,
  chipVariants,
  alertVariants,
  inputVariants,
  textFieldVariants,
  modalVariants,
  tooltipVariants,
  tabsVariants,
  accordionVariants,
  separatorVariants,
  linkVariants,
  dropdownVariants,
} from "@heroui/styles";

// ─── Button ──────────────────────────────────────────────────────────────────

/** Allowed button visual variants (excludes danger-soft). */
export type AppButtonVariant =
  | "primary"
  | "secondary"
  | "tertiary"
  | "outline"
  | "ghost"
  | "danger";

/** Allowed button sizes. */
export type AppButtonSize = "sm" | "md" | "lg";

/**
 * Button variant styles — extends HeroUI buttonVariants.
 * Returns a class string (not slot-based).
 */
export const appButtonStyles = tv({
  extend: buttonVariants,
});

// ─── Card ────────────────────────────────────────────────────────────────────

/** Allowed card visual variants (excludes transparent). */
export type AppCardVariant = "default" | "secondary" | "tertiary";

/**
 * Card variant styles — extends HeroUI cardVariants.
 * Returns slot functions: { base, content, description, footer, header, title }.
 */
export const appCardStyles = tv({
  extend: cardVariants,
});

// ─── Chip ────────────────────────────────────────────────────────────────────

/** Allowed chip semantic colors. */
export type AppChipColor = "accent" | "success" | "warning" | "danger" | "default";

/** Allowed chip visual variants. */
export type AppChipVariant = "primary" | "secondary" | "soft" | "tertiary";

/** Allowed chip sizes. */
export type AppChipSize = "sm" | "md" | "lg";

/**
 * Chip variant styles — extends HeroUI chipVariants.
 * Returns slot functions: { base, label }.
 */
export const appChipStyles = tv({
  extend: chipVariants,
});

// ─── Alert ───────────────────────────────────────────────────────────────────

/** Allowed alert status values. */
export type AppAlertStatus = "accent" | "success" | "warning" | "danger" | "default";

/**
 * Alert variant styles — extends HeroUI alertVariants.
 * Returns slot functions: { base, content, description, indicator, title }.
 */
export const appAlertStyles = tv({
  extend: alertVariants,
});

// ─── Input / TextField ───────────────────────────────────────────────────────

/** Allowed input visual variants. */
export type AppInputVariant = "primary" | "secondary";

/**
 * Input variant styles — extends HeroUI inputVariants.
 * Returns a class string (not slot-based).
 */
export const appInputStyles = tv({
  extend: inputVariants,
});

/**
 * TextField variant styles — extends HeroUI textFieldVariants.
 * Returns a class string (not slot-based). Only handles fullWidth layout.
 */
export const appTextFieldStyles = tv({
  extend: textFieldVariants,
});

// ─── Modal ───────────────────────────────────────────────────────────────────

/** Allowed modal dialog sizes (excludes cover). */
export type AppModalSize = "xs" | "sm" | "md" | "lg" | "full";

/** Allowed modal scroll behavior. */
export type AppModalScroll = "inside" | "outside";

/** Allowed modal backdrop variants. */
export type AppModalVariant = "opaque" | "blur" | "transparent";

/**
 * Modal variant styles — extends HeroUI modalVariants.
 * Returns slot functions: { backdrop, body, closeTrigger, container, dialog,
 * footer, header, heading, icon, trigger }.
 */
export const appModalStyles = tv({
  extend: modalVariants,
});

// ─── Tooltip ─────────────────────────────────────────────────────────────────

/**
 * Tooltip variant styles — extends HeroUI tooltipVariants.
 * Returns slot functions: { base, trigger }. No custom variants.
 */
export const appTooltipStyles = tv({
  extend: tooltipVariants,
});

// ─── Tabs ────────────────────────────────────────────────────────────────────

/** Allowed tabs visual variants. */
export type AppTabsVariant = "primary" | "secondary";

/**
 * Tabs variant styles — extends HeroUI tabsVariants.
 * Returns slot functions: { base, tab, tabIndicator, tabList,
 * tabListContainer, tabPanel }.
 */
export const appTabsStyles = tv({
  extend: tabsVariants,
});

// ─── Accordion ───────────────────────────────────────────────────────────────

/** Allowed accordion visual variants. */
export type AppAccordionVariant = "default" | "surface";

/**
 * Accordion variant styles — extends HeroUI accordionVariants.
 * Returns slot functions: { base, body, bodyInner, heading, indicator,
 * item, panel, trigger }.
 */
export const appAccordionStyles = tv({
  extend: accordionVariants,
});

// ─── Separator ───────────────────────────────────────────────────────────────

/** Allowed separator orientation. */
export type AppSeparatorOrientation = "horizontal" | "vertical";

/** Allowed separator visual variants. */
export type AppSeparatorVariant = "default" | "secondary" | "tertiary";

/**
 * Separator variant styles — extends HeroUI separatorVariants.
 * Returns a class string (not slot-based).
 */
export const appSeparatorStyles = tv({
  extend: separatorVariants,
});

// ─── Link ────────────────────────────────────────────────────────────────────

/**
 * Link variant styles — extends HeroUI linkVariants.
 * Returns slot functions: { base, icon }. No custom variants.
 */
export const appLinkStyles = tv({
  extend: linkVariants,
});

// ─── Dropdown ────────────────────────────────────────────────────────────────

/**
 * Dropdown variant styles — extends HeroUI dropdownVariants.
 * Returns slot functions: { menu, popover, root, trigger }. No custom variants.
 */
export const appDropdownStyles = tv({
  extend: dropdownVariants,
});
